export const WEAPON = {
  id: 'sawnoff',
  label: 'SBS-12',
  class: 'shotgun',
  caliber: '12ga',
  /** Cheap, brutal, close-range only — well under the 870-M's 1200g. */
  price: 650,
  /* `rpm` stands in for the manual cycle cadence, same convention as the pump
   * gun — but a break-action double has NOTHING to cycle between its two
   * shots (both barrels are already loaded and cocked): the only thing
   * between trigger pulls is resetting to the second trigger (or a
   * selective single trigger), which is far faster than racking a pump. */
  rpm: 240,
  modes: ['semi'],
  burstCount: 1,
  burstRpm: 240,
  burstDelay: 0.08,
  /* Two barrels, two shells. No tube, no box mag — every reload is a full
   * break-open-eject-both-reload-both cycle (see reloadEmpty below). */
  magSize: 2,
  reserve: 24,
  /* Fired as a spread of pellets — see `WeaponSystem.tryFire()`'s
   * `def.pellets` branch. `damage` below is PER PELLET.
   *
   * 00 buck runs ~9 pellets a shell (vs. the 870-M's 8 — same real load,
   * kept close). The pattern itself is what changes: a barrel sawn down to
   * ~30cm loses its choke constriction entirely (cylinder bore, or worse),
   * so the shot column opens far faster than the 870-M's factory barrel —
   * nearly double the spread. Point-blank that puts almost the whole load
   * on target for a one/two-shot kill; past a few metres it is a wide,
   * weak smear, hence the short maxRange and steep dropoff below. */
  pellets: 9,
  pelletSpread: 8.5,
  /* Cutting the barrel bleeds a little velocity (less time for powder gas
   * to push the column), but not much — most of the muzzle-velocity loss
   * from a shortened shotgun barrel happens past 45cm or so. */
  muzzleVelocity: 365,
  damage: 20,
  penetration: 0.22,
  dropoff: 2.4,
  maxRange: 16,
  dragK: 0.75,
  tracerEvery: 1,
  /* No forend to brace against, a stubby receiver, and a pistol grip only —
   * hip spread is noticeably worse than the 870-M's, and aiming down the
   * rib without a stock to reference barely tightens it. */
  spreadHip: 5.0,
  spreadAds: 2.2,
  spreadPerShot: 0.8,
  spreadMax: 5.6,
  spreadDecay: 6.0,
  recoil: {
    /* A much lighter gun firing the same 12ga load as the 870-M feels
     * dramatically more recoil per shot — there is no stock mass, no long
     * barrel weight out front, nothing to soak up the impulse. */
    pitch: 0.034,
    yaw: 0.01,
    kickBack: 0.05,
    kickUp: 0.024,
    roll: 0.09,
    punch: 1.0,
    freq: 6.0,
    damping: 0.4,
    /* Only ever fires 2 rounds before a reload, so the whole deterministic
     * pattern is those two shots. */
    patternLength: 2,
    patternSeed: 0x5a007f,
    climbShape: [1.6, 1.0],
    drift: 0.9,
  },
  /* No optic, no real sight picture to align — ADS is mostly just steadying
   * the point of aim down the rib, so it is fast and barely magnifies. */
  adsTime: 0.18,
  adsFov: 0.92,
  viewFov: 0.95,
  reloadTac: 3.4,
  /* Slower than the pump's 3.6s: break the action open, eject BOTH hulls,
   * thumb two fresh shells in by hand, snap it shut. There is no tube to
   * feed one round at a time into — it is a single, slower, deliberate
   * action every time the gun runs dry. */
  reloadEmpty: 4.6,
  inspectTime: 2.4,
  drawTime: 0.4,
  holsterTime: 0.26,
  /* ---- pose ----
   * Solved the same way as the rifle/pump: from the bore axis and the
   * muzzle's screen position, not from what looks nice. This gun is under
   * half the pump's overall length (barrels cut to 32cm vs. the 870-M's
   * 52cm, no buttstock at all), so held at the pump's hip distance
   * (z=-0.29) its short barrel and stockless grip would sit small and
   * timid in the lower-right of frame. Pulling the origin closer to the
   * eye (z=-0.235, between the pump's -0.29 and the even-more-compact
   * pistol's -0.34) keeps the muzzle, the exposed hinge pin and the
   * twin-barrel breech all inside frame and reads as the aggressive,
   * in-your-face close-range gun it is. Rotation is the same small
   * left-and-down bore convergence toward the crosshair as every other
   * gun, with slightly more roll than the pump to show the side profile
   * that actually reads as "double barrel, no forend, exposed hinge"
   * instead of looking straight down the rib. */
  hipPos: [0.1, -0.14, -0.235],
  hipRot: [-0.055, 0.085, -0.15],
  adsCant: [0, 0, 0.004],
  /* No scope tube math here (see rifle.js's eyeRelief comment for that
   * derivation) — just how far the eye sits from the rib when raised to
   * point-shoot a stockless gun: tighter than the pistol's arm's-length
   * 0.34, looser than a cheek-welded stock. */
  eyeRelief: 0.14,
  sprintPos: [0.08, -0.215, -0.21],
  sprintRot: [-0.42, 0.55, 0.22],
  lowReadyPos: [0.1, -0.235, -0.225],
  lowReadyRot: [-0.42, 0.13, -0.1],
  /* Lighter and shorter than the pump — livelier in the hands. */
  swayScale: 1.15,
  bobScale: 1.1,
  magLen: 0.08,
};

export const loadModel = () => import('../models/sawnoff.js').then((m) => m.buildSawnoff);
